import { useQuery } from '@tanstack/react-query';
import { REASON_CATEGORIES, type InventoryStatusDto, type ReasonCodeDto } from '@zenarty/shared';
import { Lock, Plus } from 'lucide-react';
import { useState } from 'react';
import { api } from '../api';
import { useAuth } from '../auth';
import { Badge, Button, Callout, Field, PageHeader, Tabs } from '../components/kit';
import { Dialog } from '../components/overlays';
import { DataTable, type Column } from '../components/table';
import { titleCase } from '../lib/format';
import { useAction } from '../lib/queries';

const yes = (b: boolean) => (b ? <Badge tone="ok">Yes</Badge> : <span className="faint">No</span>);

export function ConfigurationPage() {
  const [tab, setTab] = useState<'statuses' | 'reasons'>('statuses');
  return (
    <>
      <PageHeader
        title="Configuration"
        description="Inventory statuses decide what stock can be allocated and picked; reason codes are required on every adjustment, short, damage, return and override."
      />
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { id: 'statuses', label: 'Inventory statuses' },
          { id: 'reasons', label: 'Reason codes' },
        ]}
      />
      {tab === 'statuses' ? <Statuses /> : <Reasons />}
    </>
  );
}

function Statuses() {
  const { can } = useAuth();
  const q = useQuery({
    queryKey: ['statuses'],
    queryFn: () => api<InventoryStatusDto[]>('/inventory-statuses'),
  });
  const [editing, setEditing] = useState<InventoryStatusDto | 'new' | null>(null);
  const columns: Column<InventoryStatusDto>[] = [
    {
      id: 'name',
      header: 'Status',
      cell: (s) => (
        <div className="row">
          <strong>{s.name}</strong>
          {s.isDefault ? <Badge tone="accent">Default on receipt</Badge> : null}
          {s.system ? <Lock size={13} className="faint" aria-label="Built-in" /> : null}
        </div>
      ),
    },
    { id: 'code', header: 'Code', cell: (s) => <span className="code-pill">{s.code}</span> },
    { id: 'alloc', header: 'Allocatable', cell: (s) => yes(s.allocatable) },
    { id: 'pick', header: 'Pickable', cell: (s) => yes(s.pickable) },
  ];
  return (
    <>
      <DataTable
        columns={columns}
        rows={q.data ?? []}
        rowKey={(s) => s.id}
        loading={q.isLoading}
        onRowClick={can('config:manage') ? setEditing : undefined}
        toolbar={
          can('config:manage') ? (
            <Button size="sm" icon={Plus} onClick={() => setEditing('new')}>
              Add status
            </Button>
          ) : undefined
        }
      />
      {editing ? (
        <StatusDialog
          status={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}

function StatusDialog({ status, onClose }: { status?: InventoryStatusDto; onClose: () => void }) {
  const [f, setF] = useState({
    code: status?.code ?? '',
    name: status?.name ?? '',
    allocatable: status?.allocatable ?? false,
    pickable: status?.pickable ?? false,
    isDefault: status?.isDefault ?? false,
  });
  const save = useAction(
    () =>
      status
        ? api(`/inventory-statuses/${status.id}`, {
            method: 'PATCH',
            body: {
              name: f.name,
              allocatable: f.allocatable,
              pickable: f.pickable,
              ...(f.isDefault && !status.isDefault ? { isDefault: true } : {}),
            },
          })
        : api('/inventory-statuses', { method: 'POST', body: f }),
    {
      invalidate: [['statuses']],
      success: status ? 'Status updated' : 'Status added',
      onSuccess: onClose,
    },
  );
  return (
    <Dialog
      title={status ? `Edit ${status.name}` : 'Add an inventory status'}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(undefined)}>
            Save
          </Button>
        </>
      }
    >
      {status?.system ? (
        <Callout tone="info" title="Built-in status">
          You can rename it, but its code stays the same.
        </Callout>
      ) : null}
      <div className="form-grid">
        <Field label="Name">
          <input
            className="input"
            value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
          />
        </Field>
        <Field label="Code">
          <input
            className="input mono"
            disabled={Boolean(status)}
            value={f.code}
            onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })}
          />
        </Field>
      </div>
      <div className="stack-sm">
        <label className="check">
          <input
            type="checkbox"
            checked={f.allocatable}
            onChange={(e) => setF({ ...f, allocatable: e.target.checked })}
          />{' '}
          Allocatable — orders can reserve stock in this status
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={f.pickable}
            onChange={(e) => setF({ ...f, pickable: e.target.checked })}
          />{' '}
          Pickable — pickers may take stock in this status
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={f.isDefault}
            disabled={status?.isDefault}
            onChange={(e) => setF({ ...f, isDefault: e.target.checked })}
          />{' '}
          Default status for received stock
        </label>
      </div>
    </Dialog>
  );
}

function Reasons() {
  const { can } = useAuth();
  const q = useQuery({
    queryKey: ['reasons'],
    queryFn: () => api<ReasonCodeDto[]>('/reason-codes'),
  });
  const [category, setCategory] = useState('');
  const [editing, setEditing] = useState<ReasonCodeDto | 'new' | null>(null);
  const columns: Column<ReasonCodeDto>[] = [
    {
      id: 'name',
      header: 'Reason',
      sortValue: (r) => r.name,
      cell: (r) => (
        <strong style={r.active ? undefined : { color: 'var(--faint)' }}>{r.name}</strong>
      ),
    },
    { id: 'code', header: 'Code', cell: (r) => <span className="code-pill">{r.code}</span> },
    {
      id: 'cat',
      header: 'Used for',
      sortValue: (r) => r.category,
      cell: (r) => <span className="tag">{titleCase(r.category)}</span>,
    },
    { id: 'note', header: 'Note required', cell: (r) => yes(r.requiresNote) },
    {
      id: 'active',
      header: 'Status',
      cell: (r) =>
        r.active ? (
          <Badge tone="ok" dot>
            Active
          </Badge>
        ) : (
          <Badge dot>Retired</Badge>
        ),
    },
  ];
  return (
    <>
      <DataTable
        columns={columns}
        rows={(q.data ?? []).filter((r) => !category || r.category === category)}
        rowKey={(r) => r.id}
        loading={q.isLoading}
        searchPlaceholder="Search reasons"
        searchText={(r) => `${r.name} ${r.code}`}
        initialSort={{ id: 'cat' }}
        onRowClick={can('config:manage') ? setEditing : undefined}
        toolbar={
          <>
            <select
              className="select"
              aria-label="Category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">All uses</option>
              {REASON_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {titleCase(c)}
                </option>
              ))}
            </select>
            {can('config:manage') ? (
              <Button size="sm" icon={Plus} onClick={() => setEditing('new')}>
                Add reason
              </Button>
            ) : null}
          </>
        }
      />
      {editing ? (
        <ReasonDialog
          reason={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}

function ReasonDialog({ reason, onClose }: { reason?: ReasonCodeDto; onClose: () => void }) {
  const [f, setF] = useState({
    code: reason?.code ?? '',
    name: reason?.name ?? '',
    category: reason?.category ?? 'adjustment',
    requiresNote: reason?.requiresNote ?? false,
    active: reason?.active ?? true,
  });
  const save = useAction(
    () =>
      reason
        ? api(`/reason-codes/${reason.id}`, {
            method: 'PATCH',
            body: {
              name: f.name,
              category: f.category,
              requiresNote: f.requiresNote,
              active: f.active,
            },
          })
        : api('/reason-codes', {
            method: 'POST',
            body: {
              code: f.code,
              name: f.name,
              category: f.category,
              requiresNote: f.requiresNote,
            },
          }),
    {
      invalidate: [['reasons']],
      success: reason ? 'Reason updated' : 'Reason added',
      onSuccess: onClose,
    },
  );
  return (
    <Dialog
      title={reason ? `Edit ${reason.name}` : 'Add a reason code'}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(undefined)}>
            Save
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Name">
          <input
            className="input"
            value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
          />
        </Field>
        <Field label="Code">
          <input
            className="input mono"
            disabled={Boolean(reason)}
            value={f.code}
            onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })}
          />
        </Field>
        <Field label="Used for">
          <select
            className="select"
            value={f.category}
            onChange={(e) => setF({ ...f, category: e.target.value as typeof f.category })}
          >
            {REASON_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {titleCase(c)}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="stack-sm">
        <label className="check">
          <input
            type="checkbox"
            checked={f.requiresNote}
            onChange={(e) => setF({ ...f, requiresNote: e.target.checked })}
          />{' '}
          Require a free-text note
        </label>
        {reason ? (
          <label className="check">
            <input
              type="checkbox"
              checked={f.active}
              onChange={(e) => setF({ ...f, active: e.target.checked })}
            />{' '}
            Active (retired reasons stay in history)
          </label>
        ) : null}
      </div>
    </Dialog>
  );
}
